<template>
  <div
    :class="{
      'sb-select-inner': true,
      'sb-select-inner--error': error,
      'sb-select-inner--disabled': isDisabled,
    }"
    @keydown="handleKeyDown"
  >
    <slot name="left-icon">
      <SbIcon
        v-if="(!isLoading && leftIcon) || optionIcon"
        class="sb-select-inner__icon-left"
        :name="leftIcon || optionIcon"
        :color="leftIconColor"
        :style="{ color: leftIconColor }"
      />

      <SbAvatar
        v-else-if="isAvatarVisible"
        :src="selectedAvatar"
        :name="modelValue?.toString()"
        :data-testid="`${dataTestid}-value-avatar`"
        size="small"
      />
    </slot>

    <slot v-if="hasDefaultSlot" />

    <div class="sb-select-inner__input-wrapper">
      <slot name="selection">
        <input
          :id="inputId"
          ref="search"
          v-bind="{
            'popover-target': `sb-combobox-options-${uid}`,
            popovertargetaction: 'toggle',
          }"
          class="sb-select-inner__input"
          :aria-disabled="isDisabled || isLoading"
          :readonly="isDisabled || isLoading || disableFilter"
          :placeholder="computedPlaceholder"
          :data-testid="`${dataTestid}-search-input`"
          role="combobox"
          :aria-expanded="isOpen"
          aria-autocomplete="list"
          type="text"
          :style="inlineWidth"
          :aria-busy="isLoading"
          :aria-describedby="
            helperText ? `sb-combobox-${uid}-helper-text` : undefined
          "
          :aria-labelledby="
            labeledBy ||
            (!hiddenLabel && !inputId ? `sb-combobox-button-${uid}` : undefined)
          "
          :aria-controls="`sb-combobox-options-${uid}`"
          :aria-label="hiddenLabel"
          :value="disableFilter ? currentOptionLabel : searchInputText"
          @keydown="$emit('focus-on-input')"
          @blur="$emit('blur-on-input')"
          @input="onInput"
          @click="handleInputClick"
        />
      </slot>
      <div
        v-if="showCaption && currentOptionDetails && !isUserTyping"
        class="sb-select-inner__item-caption"
      >
        {{ currentOptionDetails }}
      </div>
      <div
        v-else-if="isUserSelect && currentOptionUser && !isUserTyping"
        class="sb-select-inner__item-caption"
        :data-testid="`${dataTestid}-user-caption`"
      >
        {{ currentOptionUser }}
      </div>
    </div>

    <div class="sb-select-inner__icons">
      <SbIcon
        v-if="!isLoading && (error || warning)"
        :name="error ? 'octagon-alert' : 'triangle-alert'"
        :data-testid="`${dataTestid}-${error ? 'error' : 'warning'}-icon`"
        :class="{
          'sb-select-inner__status-icon': true,
          'sb-select-inner__status-icon--error': error,
          'sb-select-inner__status-icon--warning': warning,
        }"
      />

      <SbIcon v-if="isLoading" name="loading" />

      <SbIconButton
        v-if="clearable && !isDisabled && modelValue"
        ref="clearButton"
        icon="x-circle"
        label="Clear Selection"
        :data-testid="`${dataTestid}-clear-button`"
        size="small"
        type="button"
        @click="clearSelection"
        @keydown.prevent.enter="clearSelection"
      />
      <slot name="rightIcon">
        <SbIconButton
          :id="'sb-combobox-button-' + uid"
          label="Open option list"
          icon="chevron-down"
          size="small"
          type="button"
          class="sb-select-inner__chevron-dropdown"
          :aria-controls="'sb-combobox-options-' + uid"
          aria-haspopup="listbox"
          :aria-expanded="isOpen"
          tabindex="-1"
          hide-tooltip
          :data-testid="`${dataTestid}-right-icon`"
        />
      </slot>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import SbAvatar from '../../Avatar'
import SbIcon, { type IconName } from '../../Icon'

import { Tooltip } from '../../../directives'
import { hasValidSearchTextLabel, useSelectContext } from '../helpers'

import type { IconColor } from '../../Icon/types'
import SbIconButton from '../../IconButton'
import type { Option, SelectValue } from '../types'

export default {
  name: 'SbSelectInner',

  directives: {
    tooltip: Tooltip,
  },

  components: {
    SbIcon,
    SbAvatar,
    SbIconButton,
  },

  props: {
    placeholder: {
      type: String,
      default: '',
    },

    uid: {
      type: String,
      default: '',
    },

    leftIcon: {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      type: String as PropType<IconName>,
      default: null,
    },

    leftIconColor: {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      type: String as PropType<IconColor>,
      default: 'primary',
    },

    optionIcon: {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      type: String as PropType<IconName>,
      default: null,
    },

    // loading props
    isLoading: Boolean,
    loadingLabel: {
      type: String,
      default: 'Loading...',
    },

    modelValue: {
      type: [String, Number] as PropType<SelectValue>,
      default: null,
    },
    options: {
      type: Array as PropType<Option[]>,
      default: () => [],
    },
    itemLabel: {
      type: String,
      default: 'label',
    },
    itemValue: {
      type: String,
      default: 'value',
    },
    searchInput: {
      type: [String, Number, null] as PropType<string | number | null>,
      default: null,
    },

    isOpen: Boolean,
    useAvatars: Boolean,
    isDisabled: Boolean,
    error: Boolean,
    warning: Boolean,

    showCaption: Boolean,
    itemCaption: {
      type: String,
      default: 'path',
    },

    disableFilter: Boolean,
    clearable: Boolean,
    inline: Boolean,

    isUserSelect: Boolean,
    labeledBy: {
      type: String,
      default: '',
    },
    hiddenLabel: {
      type: String,
      default: undefined,
    },
    inputId: {
      type: String,
      default: null,
    },
    isUserTyping: Boolean,
    helperText: {
      type: String,
      default: undefined,
    },
  },

  emits: [
    'show-list',
    'close-list',
    'search',
    'show-all-options',
    'clear-selection',
    'keydown-enter',
    'focus-on-input',
    'blur-on-input',
  ],

  setup() {
    const selectContext = useSelectContext()

    return {
      selectContext,
    }
  },

  data() {
    return {
      isFirstOpen: true,
    }
  },

  computed: {
    dataTestid() {
      return this.$attrs['data-testid'] || 'sb-select-inner'
    },

    searchInputText: {
      get() {
        if (this.isFirstOpen && this.isUserSelect) {
          return this.currentOptionLabel
        }
        if (
          hasValidSearchTextLabel(
            this.searchInput?.toString(),
            this.currentOptionLabel,
          )
        ) {
          return this.currentOptionLabel
        }

        return this.searchInput
      },
      set(value: string) {
        const shouldShowAllOptions =
          (value === this.currentOptionLabel && this.isFirstOpen) || !value
        this.$emit('show-all-options', shouldShowAllOptions)

        if (value && !value?.length) {
          this.$emit('search', undefined)
        } else {
          this.$emit('search', value)
        }

        return value
      },
    },

    computedPlaceholder() {
      return this.isLoading ? this.loadingLabel : this.placeholder
    },

    hasValue() {
      return this.modelValue !== null
    },

    hasDefaultSlot() {
      const slot =
        this.$slots && this.$slots.default ? this.$slots.default() : null
      const slotIsInner = slot && slot[0]?.key === '_innerSelect'
      const slotIsIcon = slot && slot[0]?.key === '_innerIcon'
      const slotInnerHasChildren =
        (slotIsInner || slotIsIcon) && (slot[0]?.children?.length as number) > 0
      return (
        slotInnerHasChildren ||
        ((!slotIsInner || !slotIsIcon) && slot?.length && slot?.length > 0)
      )
    },

    currentOptionLabel() {
      return this.currentOptionValue && this.currentOptionValue[this.itemLabel]
        ? this.currentOptionValue[this.itemLabel]?.toString()
        : this.modelValue?.toString()
    },

    currentOptionDetails() {
      return this.currentOptionValue &&
        this.currentOptionValue[this.itemCaption]
        ? this.currentOptionValue[this.itemCaption]
        : ''
    },

    currentOptionUser() {
      return this.currentOptionValue &&
        this.currentOptionValue[this.itemValue] &&
        this.isUserSelect &&
        this.currentOptionValue[this.itemValue] !== this.searchInputText
        ? this.currentOptionValue[this.itemValue]
        : ''
    },

    currentOptionValue() {
      if (!this.hasValue) {
        return {}
      }

      return this.options.find((opt) => opt[this.itemValue] === this.modelValue)
    },

    isAvatarVisible() {
      return (
        this.hasValue &&
        this.useAvatars &&
        Boolean(this.avatarData) &&
        !this.isUserTyping
      )
    },

    avatarData() {
      return this.options.find((option) => {
        return (
          option[this.itemValue] === this.modelValue ||
          option[this.itemLabel]?.toString().toLowerCase() ===
            this.modelValue?.toString().toLowerCase()
        )
      })
    },

    selectedAvatar() {
      return (
        this.avatarData?.src ||
        this.context.selectedItem?.src ||
        this.context.selectedItem?.avatar
      )?.toString()
    },

    context() {
      return this.selectContext()
    },

    inlineWidth() {
      const inlineLabelSize = this.currentOptionLabel
        ? this.currentOptionLabel.length
        : this.placeholder.length
      const width = this.inline ? `${inlineLabelSize}ch` : '100%'
      return { width }
    },
  },

  watch: {
    searchInputText: {
      handler(search: string) {
        this.$nextTick(() => {
          this.clearOnEmpty(search)
        })
      },
    },
    currentOptionLabel: {
      handler(label: string) {
        if (label && this.currentOptionValue && !this.isLoading) {
          this.setSearchTextToLabel(label)
        }
      },
    },
  },

  methods: {
    /**
     * clear the input on empty
     */
    clearOnEmpty(search?: string) {
      if (!search) {
        const clearButton = this.$refs.clearButton
        if (clearButton instanceof HTMLButtonElement) {
          clearButton.click()
        }
      }
    },

    /**
     * handles inner keydown element
     */
    handleKeyDown(event: KeyboardEvent) {
      if (this.isDisabled || this.isLoading) return
      event.stopPropagation()
      const { focusOnSelectedItem, focusOnFirstItem } = this.context

      if (event.key === 'Enter') {
        event.preventDefault()
        this.$emit('keydown-enter')
      }

      if (event.key === 'Escape') {
        this.$emit('close-list')
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()

        if (this.hasValue && this.searchInputText === this.currentOptionLabel) {
          focusOnSelectedItem()
        } else {
          focusOnFirstItem()
        }
      }
    },

    setSearchTextToLabel(label: string) {
      this.$emit('search', undefined)
      this.$nextTick(() => {
        if (
          hasValidSearchTextLabel(this.searchInput?.toString(), label) ||
          (this.isFirstOpen && this.isUserSelect)
        ) {
          this.searchInputText = label
        }
      })
    },

    handleInput() {
      this.isFirstOpen = false
      if (this.isDisabled || this.isLoading) return
      this.$emit('show-list', false)
    },

    clearSelection(event: Event) {
      event.stopPropagation()
      event.preventDefault()
      this.$emit('clear-selection')
    },

    handleInputClick(event: Event) {
      if (this.isOpen && !this.disableFilter) {
        event.stopPropagation()
        event.preventDefault()
      }
    },

    onInput(event: Event) {
      if (!this.disableFilter) {
        this.searchInputText = (event.target as HTMLInputElement).value
        this.handleInput()
      }
    },
  },
}
</script>
